import { AsyncPipe, CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { FormlyFieldConfig, FormlyModule } from "@ngx-formly/core";
import { BehaviorSubject, firstValueFrom, map } from "rxjs";
import { User, UserProfile, UserSettings } from "src/app/subscription/services/auth/auth.interface";
import { AuthService } from "src/app/subscription/services/auth/auth.service";
import { FormlyTabsModule } from "../ui/formly-tabs/formly-tabs.module";

@Component({
  selector: "app-settings-modal",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormlyModule,
    FormlyTabsModule,
    AsyncPipe,
  ],
  templateUrl: "./settings-modal.component.html",
  styleUrl: "./settings-modal.component.scss",
})
export class SettingsModalComponent {
  private authService = inject(AuthService);

  modalRef = inject(NgbActiveModal);

  form = new FormGroup({});
  isLoading = false;
  error?: string;

  private userSubject = new BehaviorSubject<User | null>(null);

  formFields: FormlyFieldConfig<any>[] = [
    {
      type: "tabset",
      fieldGroup: [
        {
          type: "tab",
          props: {
            tabTitle: "Account",
          },
          fieldGroup: [
            {
              key: "display_name",
              type: "input",
              props: {
                label: "Display Name",
                placeholder: "Enter your display name",
              },
            },
            {
              fieldGroupClassName: "row",
              fieldGroup: [
                {
                  className: "col-md-6",
                  key: "first_name",
                  type: "input",
                  props: {
                    label: "First Name",
                  },
                },
                {
                  className: "col-md-6",
                  key: "last_name",
                  type: "input",
                  props: {
                    label: "Last Name",
                  },
                },
              ],
            },
            {
              key: "email",
              type: "input",
              props: {
                label: "Email",
                disabled: true,
              },
            },
          ],
        },
        {
          type: "tab",
          props: {
            tabTitle: "Teaching Info",
          },
          fieldGroup: [
            {
              fieldGroupClassName: "row",
              fieldGroup: [{
                className: "col-md-6",
                key: "grade_levels",
                type: "multicheckbox",
                props: {
                  label: "Grade Level(s)",
                  description: "Select the grade levels you teach",
                  type: "array",
                  options: [
                    { value: "Pre-K", label: "Pre-K" },
                    { value: "Kindergarten", label: "Kindergarten" },
                    { value: "1st Grade", label: "1st Grade" },
                    { value: "2nd Grade", label: "2nd Grade" },
                    { value: "3rd Grade", label: "3rd Grade" },
                    { value: "4th Grade", label: "4th Grade" },
                    { value: "5th Grade", label: "5th Grade" },
                    { value: "6th Grade", label: "6th Grade" },
                    { value: "7th Grade", label: "7th Grade" },
                    { value: "8th Grade", label: "8th Grade" },
                    { value: "9th Grade", label: "9th Grade" },
                    { value: "10th Grade", label: "10th Grade" },
                    { value: "11th Grade", label: "11th Grade" },
                    { value: "12th Grade", label: "12th Grade" },
                    { value: "Adult", label: "Adult" },
                  ],
                },
              }, {
                className: "col-md-6",
                key: "subject_areas",
                type: "multicheckbox",
                props: {
                  label: "Subject Areas",
                  description: "Select the subjects you teach",
                  type: "array",
                  options: [
                    {
                      value: "English/Language Arts",
                      label: "English/Language Arts",
                    },
                    { value: "Math", label: "Math" },
                    { value: "Science", label: "Science" },
                    { value: "Social Studies", label: "Social Studies" },
                    { value: "Art", label: "Art" },
                    { value: "Music", label: "Music" },
                    {
                      value: "Physical Education",
                      label: "Physical Education",
                    },
                    { value: "Health", label: "Health" },
                    { value: "Technology", label: "Technology" },
                    { value: "Foreign Language", label: "Foreign Language" },
                    { value: "Special Education", label: "Special Education" },
                    { value: "Other", label: "Other" },
                  ],
                },
              }],
            },
            {
              key: "school_type",
              type: "select",
              props: {
                label: "School Type",
                description: "Type of school environment you teach in",
                placeholder: "Select school type",
                options: [
                  { value: "Public School", label: "Public School" },
                  { value: "Private School", label: "Private School" },
                  { value: "Charter School", label: "Charter School" },
                  { value: "Homeschool", label: "Homeschool" },
                  { value: "Other", label: "Other" },
                ],
              },
            },
            {
              key: "teaching_state",
              type: "select",
              props: {
                label: "Teaching State",
                description: "State where you teach",
                placeholder: "Select state",
                options: [
                  { value: "Alabama", label: "Alabama" },
                  { value: "Alaska", label: "Alaska" },
                  { value: "Arizona", label: "Arizona" },
                  { value: "Arkansas", label: "Arkansas" },
                  { value: "California", label: "California" },
                  { value: "Colorado", label: "Colorado" },
                  { value: "Connecticut", label: "Connecticut" },
                  { value: "Delaware", label: "Delaware" },
                  { value: "Florida", label: "Florida" },
                  { value: "Georgia", label: "Georgia" },
                  { value: "Hawaii", label: "Hawaii" },
                  { value: "Idaho", label: "Idaho" },
                  { value: "Illinois", label: "Illinois" },
                  { value: "Indiana", label: "Indiana" },
                  { value: "Iowa", label: "Iowa" },
                  { value: "Kansas", label: "Kansas" },
                  { value: "Kentucky", label: "Kentucky" },
                  { value: "Louisiana", label: "Louisiana" },
                  { value: "Maine", label: "Maine" },
                  { value: "Maryland", label: "Maryland" },
                  { value: "Massachusetts", label: "Massachusetts" },
                  { value: "Michigan", label: "Michigan" },
                  { value: "Minnesota", label: "Minnesota" },
                  { value: "Mississippi", label: "Mississippi" },
                  { value: "Missouri", label: "Missouri" },
                  { value: "Montana", label: "Montana" },
                  { value: "Nebraska", label: "Nebraska" },
                  { value: "Nevada", label: "Nevada" },
                  { value: "New Hampshire", label: "New Hampshire" },
                  { value: "New Jersey", label: "New Jersey" },
                  { value: "New Mexico", label: "New Mexico" },
                  { value: "New York", label: "New York" },
                  { value: "North Carolina", label: "North Carolina" },
                  { value: "North Dakota", label: "North Dakota" },
                  { value: "Ohio", label: "Ohio" },
                  { value: "Oklahoma", label: "Oklahoma" },
                  { value: "Oregon", label: "Oregon" },
                  { value: "Pennsylvania", label: "Pennsylvania" },
                  { value: "Rhode Island", label: "Rhode Island" },
                  { value: "South Carolina", label: "South Carolina" },
                  { value: "South Dakota", label: "South Dakota" },
                  { value: "Tennessee", label: "Tennessee" },
                  { value: "Texas", label: "Texas" },
                  { value: "Utah", label: "Utah" },
                  { value: "Vermont", label: "Vermont" },
                  { value: "Virginia", label: "Virginia" },
                  { value: "Washington", label: "Washington" },
                  { value: "West Virginia", label: "West Virginia" },
                  { value: "Wisconsin", label: "Wisconsin" },
                  { value: "Wyoming", label: "Wyoming" },
                  { value: "Outside US", label: "Outside US" },
                ],
              },
            },
            {
              key: "biggest_challenge",
              type: "select",
              props: {
                label: "Biggest Challenge",
                description: "Your biggest classroom challenge",
                placeholder: "Select challenge",
                options: [
                  {
                    value: "Lesson planning takes too much time",
                    label: "Lesson planning takes too much time",
                  },
                  {
                    value: "Keeping students engaged",
                    label: "Keeping students engaged",
                  },
                  {
                    value: "Managing classroom behavior",
                    label: "Managing classroom behavior",
                  },
                  {
                    value: "Tracking student progress",
                    label: "Tracking student progress",
                  },
                  {
                    value: "Organizing materials and resources",
                    label: "Organizing materials and resources",
                  },
                  {
                    value: "Communication with parents",
                    label: "Communication with parents",
                  },
                  { value: "Other", label: "Other" },
                ],
              },
            },
          ],
        },
        {
          type: "tab",
          props: {
            tabTitle: "Features",
          },
          fieldGroup: [
            {
              key: "markdown_line_numbers",
              type: "checkbox",
              props: {
                label: "Show Line Numbers in Markdown Editor",
                description:
                  "When enabled, line numbers will be displayed in the markdown editor.",
              },
            },
            {
              key: "bible_enabled",
              type: "checkbox",
              props: {
                label: "Enable Bible",
                description:
                  "When enabled, you'll have access to Bible verses and related features throughout the application.",
              },
            },
            {
              key: "jax_agent_version",
              type: "select",
              props: {
                label: "JAX Agent Version",
                description:
                  "Choose which version of the JAX AI assistant to use. v1 is the stable version, v2 uses an experimental plan-based approach for document generation.",
                options: [
                  { value: "v1", label: "v1 - Stable" },
                  { value: "v2", label: "v2 - Experimental (Plan-based)" },
                ],
              },
            },
          ],
        },
      ],
    },
  ];

  model$ = this.userSubject.asObservable().pipe(
    map((user) => {
      if (!user) return {};

      return {
        display_name: user.display_name || "",
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        grade_levels: Array.isArray(user.grade_levels) ? user.grade_levels : [],
        subject_areas: Array.isArray(user.subject_areas) ? user.subject_areas : [],
        school_type: user.school_type || "",
        teaching_state: user.teaching_state || "",
        biggest_challenge: user.biggest_challenge || "",
        markdown_line_numbers: user.markdown_line_numbers ?? false,
        bible_enabled: user.bible_enabled ?? false,
        jax_agent_version: user.jax_agent_version || "v1",
      };
    }),
  );

  constructor() {
    // Subscribe to user changes
    this.authService.$user.pipe(
      takeUntilDestroyed(),
    ).subscribe((user: User | null) => {
      this.userSubject.next(user);
    });
  }

  closeModal() {
    this.modalRef.dismiss();
  }

  async save(formData: any) {
    const user = this.userSubject.value;
    if (!user || !this.form.valid) {
      return;
    }

    this.isLoading = true;
    this.error = undefined;

    const settingsData: Partial<UserSettings> = {
      grade_levels: Array.isArray(formData.grade_levels) ? formData.grade_levels : [],
      subject_areas: Array.isArray(formData.subject_areas) ? formData.subject_areas : [],
      school_type: formData.school_type || null,
      teaching_state: formData.teaching_state || null,
      biggest_challenge: formData.biggest_challenge || null,
      markdown_line_numbers: formData.markdown_line_numbers ?? false,
      bible_enabled: formData.bible_enabled ?? false,
      jax_agent_version: formData.jax_agent_version || "v1",
    };

    const profileData: Partial<UserProfile> = {
      display_name: formData.display_name ?? null,
      first_name: formData.first_name?.trim() || null,
      last_name: formData.last_name?.trim() || null,
    };

    try {
      await Promise.all([
        firstValueFrom(this.authService.updateUserSettings(user.id, settingsData)),
        firstValueFrom(this.authService.updateUserProfile(user.id, profileData)),
      ]);
      this.isLoading = false;
      this.modalRef.close(true);
    } catch (err: any) {
      this.isLoading = false;
      this.error = err.message;
    }
  }

  get hasChanges(): boolean {
    return this.form.dirty && this.form.valid;
  }
}
